import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { pcmpProposalDetailQueryOptions } from '../../api/pcmpProposals.api';
import type { PcmpProposalDetail } from '../../types/pcmpProposal.types';

interface PcmpProposalDialogLoaderProps {
  proposalId: number;
  title: string;
  onClose: () => void;
  /** Rendered once the proposal is loaded; a cache hit from its detail page. */
  children: (proposal: PcmpProposalDetail) => ReactNode;
}

/** A dialog opened from a queue row, which carries no account details: load them first. */
export function PcmpProposalDialogLoader({
  proposalId,
  title,
  onClose,
  children,
}: PcmpProposalDialogLoaderProps) {
  const { data, error, isFetching, refetch } = useQuery(pcmpProposalDetailQueryOptions(proposalId));

  if (data) return children(data);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {error ? (
          <ErrorState
            error={error}
            fallbackTitle="Could not load the proposal"
            onRetry={() => void refetch()}
            isRetrying={isFetching}
          />
        ) : (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading the proposal...
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
